import {
  Bot,
  Layers,
  Activity,
  Webhook,
  Users,
  Palette,
  SlidersHorizontal,
  KeyRound,
  Cpu,
  Puzzle,
  Database,
  Archive,
  ShieldCheck,
  UserCog,
  UsersRound,
  BadgeCheck,
  Building2,
  Landmark,
  Plug,
  Megaphone,
  Sparkles,
  KeySquare,
  Wrench,
  HardDrive,
  Mail,
  Share2,
} from 'lucide-react';
import type { NavItem } from './nav-types';
import type { NavCardItem } from '@/components/shared/NavCardList';
import { ROUTES } from '@/lib/routes';

/**
 * Builds the main (top-of-sidebar) nav items for an organization. Inbox is
 * rendered as a compact icon next to the user avatar in `UserSection`, not
 * here, so this list only carries top-level destinations that aren't tied to
 * account state.
 *
 * Pass the active slug from `useActiveOrgSlug()`.
 */
export function buildMainNavItems(slug: string): NavItem[] {
  const r = ROUTES.org(slug);

  return [{ to: r.runs, label: 'Sessions', icon: Bot, tutorialId: 'nav-agents' }];
}

export function buildCoreMiscNavItems(slug: string): NavItem[] {
  void slug;
  // MCP client setup lives under Connect → MCP; Misc no longer has a
  // host-owned core entry. Plugin-contributed misc pages still appear.
  return [];
}

/**
 * Re-order a list so that any entry whose `to` appears in `bottomRoutes` is
 * moved to the end, preserving the relative order both within the top and
 * bottom groups. Used by overview pages (Misc, Integrations, …) that want
 * certain entries pinned to the bottom of their card grid.
 */
export function applyBottomRoutes<T extends { to: string }>(
  items: readonly T[],
  bottomRoutes: readonly string[],
): T[] {
  if (bottomRoutes.length === 0) return [...items];
  const bottomSet = new Set(bottomRoutes);
  const top: T[] = [];
  const bottom: T[] = [];
  for (const item of items) {
    if (bottomSet.has(item.to)) bottom.push(item);
    else top.push(item);
  }
  return [...top, ...bottom];
}

/**
 * Core nav items in the "integrations" group. Empty by default — every
 * integration (GitHub, Pylon, Linear, Slack, Sentry, …) is contributed by
 * its plugin's `web-manifest.ts` with `group: 'integrations'`. The function
 * shape is preserved so plugin/host code can register host-owned integration
 * entries here in the future without rippling new arguments through callers.
 */
export function buildCoreIntegrationNavItems(): NavItem[] {
  return [];
}

/**
 * Core nav items for the "Security" drill group. Today this is just the
 * personal Model Providers page — the team/org-tier Model Providers
 * pages are reached via in-page sibling links from the personal page
 * (and from the Organization drill / team detail header) rather than
 * cluttering the sidebar with three near-identical entries.
 */
export function buildCoreSecurityNavItems(slug: string): NavItem[] {
  const r = ROUTES.org(slug);
  return [
    {
      to: r.modelProvidersPersonal,
      label: 'Model Providers',
      icon: Sparkles,
      tutorialId: 'nav-model-providers',
      description: 'Personal model API keys and subscriptions.',
      details:
        'Manage your personal Anthropic, OpenAI, Google, Fireworks AI, Baseten, xAI, and OpenRouter API keys plus subscription credentials (Claude Max, Codex). Personal keys take precedence over team and org keys when launching agents under your account, and team/org keys you can manage are reachable from here as well.',
      features: [
        'Per-provider API keys',
        'Claude Max / Codex subscriptions',
        'Team & org keys you manage',
      ],
    },
    {
      to: r.securitySecrets,
      label: 'Secrets',
      icon: KeyRound,
      description: 'Personal key/value secrets.',
      details:
        'Store arbitrary user-tier key/value secrets: tokens you do not want to share with the rest of your organization. Each secret is mounted into every agent you launch as an environment variable, scoped to your user and the active organization, and visible only to you.',
      features: [
        'Personal key/value pairs',
        'Injected into your agents as env vars',
        'Per-organization, private to you',
      ],
    },
    {
      to: r.securityApiKeys,
      label: 'API keys',
      icon: KeySquare,
      description: 'Personal keys for the Reflex API.',
      details:
        'Create and revoke personal API keys that call the Reflex API as you. Use them with the @runloop/reflex-client SDK or any HTTP client. Each token is shown once at creation, then only its prefix is visible.',
      features: ['Create and revoke keys', 'Token shown once', 'Works with the SDK'],
    },
    {
      to: r.securitySharing,
      label: 'Sharing',
      icon: Share2,
      description: 'Who can reach your sessions, and for how long.',
      details:
        'Review and manage your shared resources: sessions shared with you, sessions you have shared, and, for org admins, every share in the organization. Extend a share or stop sharing at any time.',
      features: ['Shared with you', 'Shared by you', 'Extend shares', 'Stop sharing any time'],
    },
  ];
}

export function buildCoreOrganizationNavItems(slug: string): NavItem[] {
  const r = ROUTES.org(slug);
  return [
    {
      to: r.members,
      label: 'Members',
      icon: UserCog,
      tutorialId: 'nav-members',
      description: 'People in this organization.',
      details:
        'See who belongs to the active organization, what roles they hold, and remove or re-role members. Org admins can change member roles; everyone with access can review the roster.',
      features: ['Member roster', 'Role assignments', 'Remove members'],
      permission: 'org:read',
    },
    {
      to: r.teams,
      label: 'Teams',
      icon: UsersRound,
      tutorialId: 'nav-teams',
      description: 'Sub-groups inside this organization.',
      details:
        'Group members into teams to organize ownership and surface team-level activity. Teams are scoped to the active organization and can be referenced by integrations and routing rules.',
      features: ['Create teams', 'Assign members', 'Per-team detail pages'],
      permission: 'teams:read',
    },
    {
      to: r.roles,
      label: 'Roles',
      icon: BadgeCheck,
      tutorialId: 'nav-roles',
      description: 'Roles and permissions.',
      details:
        'Inspect built-in roles (owner, admin, member) and create custom roles with bespoke permission sets. Roles are how members get access to specific features inside the organization.',
      features: ['Built-in roles', 'Custom roles', 'Permission inspection'],
      permission: 'org:read',
    },
    {
      to: r.secrets,
      label: 'Secrets',
      icon: KeyRound,
      tutorialId: 'nav-secrets',
      description: 'Encrypted credential store.',
      details:
        'Store API keys, tokens, and other sensitive values that agents and integrations need at runtime. Secrets are encrypted at rest and injected into devboxes securely.',
      features: ['Encrypted storage', 'Per-agent injection', 'Rotation support'],
      permission: 'secrets:read',
    },
    {
      to: r.orgModelProviders,
      label: 'Model Providers',
      icon: Sparkles,
      tutorialId: 'nav-org-model-providers',
      description: 'Org-wide model API keys.',
      details:
        'Manage the Anthropic, OpenAI, Google, Fireworks AI, Baseten, and xAI API keys used by every agent launched in this organization that does not have a personal or team-tier key. Updated keys apply to agents launched after the change.',
      features: ['Per-provider keys', 'Encrypted storage', 'Org-wide fallback'],
      permission: 'secrets:read',
    },
    {
      to: r.blueprints,
      label: 'Blueprints',
      icon: Layers,
      tutorialId: 'nav-blueprints',
      description: 'Reproducible devbox environments.',
      details:
        'Blueprints are Dockerfile-based templates that define the operating system, packages, and setup commands for agent devboxes. Build a blueprint once and every agent launched from it starts with an identical, pre-configured environment: no drift, no missing dependencies.',
      features: [
        'Dockerfile-driven',
        'Custom install commands',
        'File mounts',
        'Shared across agents',
      ],
    },
    {
      to: r.agentsHub,
      label: 'Agents',
      icon: Cpu,
      tutorialId: 'nav-agent-models',
      description: 'Personas and agent model policy.',
      details:
        'Manage reusable agent personas and enable or disable AI models, providers, and Launch dialog defaults for this organization.',
      features: ['Personas', 'Provider toggles', 'Model filtering', 'Default selection'],
    },
    {
      to: r.integrations,
      label: 'Integrations',
      icon: Plug,
      tutorialId: 'nav-integrations',
      description: 'GitHub, Pylon, Linear, Slack, Sentry.',
      details:
        'Configure third-party connections used by agents in this organization. Each integration is per-org so credentials never leak across tenants.',
      features: ['Per-org credentials', 'Webhook receivers', 'MCP tools'],
      permission: 'integrations:read',
    },
    {
      to: r.sharingSettings,
      label: 'Sharing',
      icon: Share2,
      description: 'Org-wide limits on session sharing.',
      details:
        'Decide whether members may share their sessions read-only or with edit access. Turning a level off stops the next share at that level for every caller; shares that are already open stay in place.',
      features: ['View sharing switch', 'Edit sharing switch', 'Enforced server-side'],
      // Readable like the sibling org rows: members can see the policy that
      // shapes their share picker; edits are gated on org:manage in-page.
      permission: 'org:read',
    },
    {
      to: r.company,
      label: 'Company',
      // Not `Building2`: that is the Organization row's mark, and these two
      // sit next to each other. One company mark, used by this row and by
      // both Company page headers.
      icon: Landmark,
      description: 'The company this organization belongs to.',
      details:
        'A company owns one or more organizations. It holds the contract, the plan, and the identity provider they share, while members, roles, agents, and secrets stay inside each organization.',
      features: ['Plan', 'Organizations covered'],
      // Recorded like the sibling org rows, but note it does not hide this
      // one: core organization items are deliberately not permission-filtered
      // into the Organization drill (see `useNavSources`), so the row stays
      // listed and the page body's `HubReadGuard` names the missing slug. The
      // server is the fence — `company:read` is outside the derived read set,
      // so an ordinary viewer cannot enumerate the company's organizations.
      permission: 'company:read',
    },
    {
      to: r.organizationHub,
      label: 'Organization',
      icon: Building2,
      tutorialId: 'nav-org-settings',
      description: 'General settings and service accounts.',
      details:
        'Rename the active organization, change its URL slug, and manage machine identities with their own API keys.',
      features: ['Rename organization', 'Change slug', 'Service accounts'],
      permission: 'org:read',
    },
  ];
}

/**
 * Admin nav items are NOT org-scoped — admin lives at the root level.
 */
export const adminNavItems: NavItem[] = [
  {
    to: ROUTES.admin.monitor,
    label: 'Monitor',
    icon: Activity,
    description: 'Real-time agent dashboard.',
    details:
      'See all active agents at a glance with live status updates, who launched them, how long they have been running, and per-user activity breakdowns.',
    features: ['Live agent status', 'Team activity', 'Running duration', 'Per-user breakdown'],
    permission: 'agents:read',
  },
  {
    to: ROUTES.admin.webhooks,
    label: 'Webhooks',
    icon: Webhook,
    description: 'Incoming webhook events.',
    details:
      'Inspect recent webhook deliveries from GitHub, Linear, Sentry, and other integrations. See payloads, response codes, and timing to diagnose integration issues quickly.',
    features: ['Delivery history', 'Payload inspection', 'Status codes', 'Retry tracking'],
    permission: 'integrations:read',
  },
  {
    to: ROUTES.admin.users,
    label: 'Users',
    icon: Users,
    description: 'Team member management.',
    details:
      'Browse all users who have accessed the platform. See when they joined, their recent activity, and how many agents they have launched.',
    features: ['User list', 'Join dates', 'Activity tracking', 'Agent counts'],
    permission: 'members:manage',
  },
  {
    to: ROUTES.admin.organizations,
    label: 'Organizations',
    icon: Building2,
    description: 'All organizations in the system.',
    details:
      'View every organization being managed by this instance, including personal workspaces and shared tenants. See when each organization was created and who owns it.',
    features: ['List all orgs', 'Personal vs shared', 'Created date'],
    permission: 'admin:manage',
  },
  {
    to: ROUTES.admin.companies,
    label: 'Companies',
    // Landmark, the company mark the org-side Company row and pages use.
    icon: Landmark,
    description: 'Companies and their shared settings.',
    details:
      'Browse every company on this instance. Open one to change its plan and manage the email domains it proves with DNS, which route SSO discovery and auto-join for all of its organizations.',
    features: ['List all companies', 'Plan', 'Email domains'],
    permission: 'admin:manage',
  },
  {
    to: ROUTES.admin.appearance,
    label: 'Appearance',
    icon: Palette,
    description: 'Default color theme.',
    details:
      'Set the default color theme for all users. Users who have not picked a personal theme will see this palette. Individual users can override it from their Settings page.',
    features: ['Default theme', 'Color palette', 'User-overridable'],
    permission: 'org:manage',
  },
  {
    to: ROUTES.admin.settings,
    label: 'Settings',
    icon: HardDrive,
    description: 'Deployment-wide Reflex limits.',
    details:
      'Set limits that apply across the Reflex service. The sandbox disk ceiling controls the largest disk organizations can assign to a custom sandbox size.',
    features: ['Sandbox disk ceiling', 'All organizations', 'No redeploy needed'],
    permission: 'admin:manage',
  },
  {
    to: ROUTES.admin.email,
    label: 'Email',
    icon: Mail,
    description: 'Transactional email status.',
    details:
      'See how transactional email is configured for this deployment: whether sending is enabled, the provider and sender address, the data-residency region, and which emails Reflex can send. Configured via environment variables, so this view is read-only.',
    features: ['Sending status', 'Provider and sender', 'Read-only'],
    permission: 'admin:manage',
  },
  {
    to: ROUTES.admin.auth,
    label: 'Auth',
    icon: ShieldCheck,
    description: 'Sign-in domain allowlist.',
    details:
      'Restrict which email domains are allowed to sign in across every configured OAuth provider. Leave the list empty to permit any domain. Changes take effect immediately for new logins.',
    features: ['Domain allowlist', 'All providers', 'Instant propagation'],
    permission: 'admin:manage',
  },
  {
    to: ROUTES.admin.maintenance,
    label: 'Maintenance',
    icon: Wrench,
    description: 'Site-wide maintenance banner.',
    details:
      'Turn on a persistent banner shown above the main content on every page, with a message you write. The app stays fully usable; the banner clears only when you turn maintenance mode off.',
    features: ['Custom message', 'Shown to all users', 'App stays usable'],
    permission: 'admin:manage',
  },
  {
    to: ROUTES.admin.flags,
    label: 'Feature Flags',
    icon: SlidersHorizontal,
    description: 'Runtime feature toggles.',
    details:
      'Enable or disable features at runtime without redeploying. Feature flags let you roll out new functionality gradually, run experiments, or quickly kill-switch a misbehaving feature.',
    features: ['Toggle on/off', 'No redeploy needed', 'Instant propagation'],
    // No `permission`: global flags and per-user overrides are deployment-wide,
    // so the routes sit on the platform-admin tier that gates `/admin` itself.
  },
  {
    to: ROUTES.admin.serviceSecrets,
    label: 'Service Secrets',
    icon: KeyRound,
    description: 'Operator-managed credentials walled off from organizations.',
    details:
      'GitHub App credentials, OAuth user tokens, and internal infrastructure secrets. Never mirrored into Runloop and never visible to org admins. Super-admin only.',
    features: ['Service-tier wall', 'Super-admin only', 'No Runloop mirroring'],
    permission: 'service-secrets:manage',
  },
  {
    to: ROUTES.admin.plugins,
    label: 'Plugins',
    icon: Puzzle,
    description: 'Installed plugin management.',
    details:
      'View and configure installed plugins that extend the platform with new integrations, pages, and agent capabilities. Each plugin is self-contained and independently versioned.',
    features: ['Plugin list', 'Configuration', 'Enable/disable', 'Version info'],
    permission: 'plugins:manage',
  },
  {
    to: ROUTES.admin.database,
    label: 'Database',
    icon: Database,
    description: 'Database viewer.',
    details:
      'Browse, search, and edit database tables directly. View schemas, paginate through rows, edit cells inline, insert or delete rows, and run arbitrary SQL queries.',
    features: ['Table browser', 'Inline editing', 'Row CRUD', 'SQL console'],
    permission: 'database:manage',
  },
  {
    to: ROUTES.admin.seeds,
    label: 'Seeds',
    icon: Archive,
    description: 'Export and import organization data.',
    details:
      'Export a full portable bundle of your organization configuration, then import it into another environment. Credentials travel via an encrypted artifact. Progress tracking shows per-kind status during import.',
    features: ['Full-org export', 'Bundle upload', 'Progress tracking', 'Idempotent'],
    // No `permission`: `/seeds/*` is gated on the platform-admin tier rather
    // than an org-grantable slug, and the Admin drill this item lives in is
    // already `requirePlatformAdmin`. A slug here would have to be one the
    // catalog knows, and any such slug is assignable to a custom org role —
    // which is exactly what the tier change closed.
  },
  {
    to: ROUTES.admin.notifications,
    label: 'Notifications',
    icon: Megaphone,
    description: 'Publish broadcasts to users.',
    details:
      'Author and send a notification to every user, a single organization, or a single user. Recipients see it in their inbox sidebar and as a toast on next refresh.',
    features: ['Global / org / user scope', 'Markdown body', 'Optional link', 'Optional expiry'],
    permission: 'admin:manage',
  },
];

/**
 * Filters a list of nav items to those the current user is allowed to see.
 *
 * An item is visible when it has no `permission` requirement, when the user
 * is a platform admin (super admin OR app admin — the same union the server
 * uses for bypass), or when the user's `can()` check passes for the item's
 * permission. When auth is disabled (single-tenant default) every item is
 * visible — callers should pass a permissive `can` function in that case.
 */
export function filterNavItemsByPermission<T>(
  items: readonly T[],
  can: (permission: string) => boolean,
  isPlatformAdmin = false,
): T[] {
  if (isPlatformAdmin) return [...items];
  return items.filter((item) => {
    const perm = (item as { permission?: string }).permission;
    return !perm || can(perm);
  });
}

export function toNavCardItems(items: readonly NavItem[]): NavCardItem[] {
  return items
    .filter(
      (i): i is NavItem & { description: string; details: string; features: readonly string[] } =>
        !!i.description && !!i.details && !!i.features,
    )
    .map(({ to, label, icon, description, details, features, tutorialId }) => ({
      to,
      label,
      icon,
      description,
      details,
      features,
      ...(tutorialId ? { tutorialId } : {}),
    }));
}

interface PluginNavLike {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface PluginOverviewCardLike extends NavCardItem {
  group: string;
}

/**
 * Merge core nav items, explicit plugin overview cards, and plugin nav items
 * into a single card list. Plugin nav items that lack a matching overview card
 * get auto-promoted to a minimal card so they still appear on overview pages.
 *
 * Any card whose `to` appears in `bottomRoutes` is moved to the end of the
 * returned list, preserving the original relative order among themselves.
 */
export function mergeNavAndPluginCards(
  coreNavItems: readonly NavItem[],
  pluginOverviewCards: readonly PluginOverviewCardLike[],
  pluginNavItems: readonly PluginNavLike[],
  group: string,
  bottomRoutes: readonly string[] = [],
): NavCardItem[] {
  const coreCards = toNavCardItems(coreNavItems);
  const explicitCards = pluginOverviewCards.filter((c) => c.group === group);

  const coveredRoutes = new Set([...coreCards.map((c) => c.to), ...explicitCards.map((c) => c.to)]);

  const autoCards: NavCardItem[] = pluginNavItems
    .filter((n) => !coveredRoutes.has(n.to))
    .map(({ to, label, icon }) => ({
      to,
      label,
      icon,
      description: label,
      details: `Go to ${label}.`,
      features: [],
    }));

  return applyBottomRoutes([...coreCards, ...explicitCards, ...autoCards], bottomRoutes);
}
